import type { BlocksContent } from "@strapi/blocks-react-renderer";
import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo, useState } from "react";

import { CommonBlocksRenderer } from "@widgets/common-blocks-renderer";

import { Accordion } from "@shared/ui/accordion/accordion";
import { Body, Heading } from "@shared/ui/typography";
import {
  ApplyReplacements,
  getTitleWithIconData,
} from "@shared/ui/typography/apply-replacements-to-text";

import type { FAQSectionType } from "../../model";

import s from "./faq-section.module.scss";

export type CompProps = ComponentProps<"div"> & {
  data: FAQSectionType;
};

export const FAQSection = (props: CompProps) => {
  const { data } = props;

  const titleSetting = useMemo(() => {
    return getTitleWithIconData(data.titleWithIcon);
  }, [data.titleWithIcon]);

  const initialAccordion = data?.faqContent
    ? `${data?.faqContent[0].id}`
    : null;

  const [activeAccordion, setActiveAccordion] = useState<string | null>(
    initialAccordion,
  );

  const handleOpenAccordion = useCallback((id: string) => {
    setActiveAccordion((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div className={clsx(s.root)}>
      <div className={s.main}>
        {data.subtitle && (
          <Body size="uppercase-small" className={s.subtitle}>
            {data.subtitle}
          </Body>
        )}
        {titleSetting.text && (
          <Heading level={3} aria-label={titleSetting.text}>
            <ApplyReplacements
              text={titleSetting.text}
              settings={titleSetting.settings}
            />
          </Heading>
        )}
      </div>
      <div className={s.accordions}>
        {data?.faqContent?.length > 0 &&
          data?.faqContent?.map(
            (item) =>
              item.content && (
                <Accordion
                  key={item.id}
                  title={item.title}
                  id={`${item.id}`}
                  className={s.accordion}
                  isOnceOpen
                  activeAccordion={activeAccordion}
                  onAccordionClick={handleOpenAccordion}
                >
                  <CommonBlocksRenderer
                    data={item.content as unknown as BlocksContent}
                  />
                </Accordion>
              ),
          )}
      </div>
      <div className={s.description}>
        {data?.description && (
          <CommonBlocksRenderer
            data={data.description as unknown as BlocksContent}
          />
        )}
      </div>
    </div>
  );
};

FAQSection.displayName = "FAQSection";
